import { AI_SERVICES_ORIGIN, MOSIDA_API_ORIGIN } from "../config/apiUrls";
import { fetchWithTimeout } from "../utils/httpClient";
import { aiSelfieArtStrictReferenceImageUrl } from "./aiSelfieArtService";
import { fetchImage } from "./imageReadService";
import {
    buildCharacterPromptForArt,
    isCaricatureHobbyAiSelfieArt,
    isCaricatureOrCartoonArt,
    isCaricaturePortraitFlow,
    isPrintYourOwnFlow,
    readPortraitSubject,
    type TransformationProcessPreset,
} from "./stylePromptResolver";
import type { AiSelfieArt } from "./aiSelfieArtService";

/** Multipart style transfer: user photo + reference art image + prompt (non–caricature-hobby). */
export type ReferenceStyleParams = {
    referenceImageUrl: string;
};

export interface ProcessImageParams {
    imageurl: string;
    prompt: string;
    background: any;
    /** Optional passthrough from ai-selfie-arts/active background_image field. */
    background_image?: string | null;
    position?: string;
    canvas_size?: string;
    station?: string;
    remove_bg?: any;
    /** Appended after standard fields; use for API-driven extras (last value wins on duplicate keys if the server merges that way). */
    extraFormFields?: Record<string, string>;
    /**
     * When set with a non-empty `referenceImageUrl`, POSTs to `generate-reference-style-web`
     * instead of `/api/generate-character`.
     */
    referenceStyle?: ReferenceStyleParams;
    /** Passed through from active selfie art API when available. */
    temperature?: string | number | null;
    /** Optional logo placement from AI Station API. */
    logo_position?: string | null;
    /** When true, generate-character-web skips Mosida logo / keeps transparency. */
    is_transparent?: boolean;
    /** Print Your Own (snap & print): request logo on generated art. */
    is_logo?: boolean;
    /**
     * Print Your Own / snap-print: POST `/creative-system/generate`
     * (Mosida logo stamp) instead of generate-character-web.
     */
    useMosidaLogoGenerate?: boolean;
}

const RESERVED_ADDITIONAL_KEYS = new Set([
    "background_url",
    "backgroundUrl",
    "background",
    "canvas_size",
    "canvasSize",
    "remove_bg",
    "removeBg",
    "position",
    "prompt",
    "negative_prompt",
    "character_prompt",
    "selfie",
    "selfie_url",
    "temperature",
    "temparature",
    "logo_postition",
    "logo_position",
]);

export function parseAdditionalParamsFromApi(raw: unknown): Record<string, unknown> {
    if (raw == null || raw === "") return {};
    if (typeof raw === "string") {
        try {
            const parsed = JSON.parse(raw) as unknown;
            if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
                return parsed as Record<string, unknown>;
            }
            return {};
        } catch {
            return {};
        }
    }
    if (typeof raw === "object" && !Array.isArray(raw)) {
        return raw as Record<string, unknown>;
    }
    return {};
}

function pickFirst<T>(obj: Record<string, unknown>, keys: string[]): T | undefined {
    for (const k of keys) {
        const v = obj[k];
        if (v !== undefined && v !== null) return v as T;
    }
    return undefined;
}

function parseTemperatureFromAdditional(additional: Record<string, unknown>): string | number | null | undefined {
    const raw = pickFirst<unknown>(additional, ["temperature", "temparature"]);
    return normalizeTemperature(raw);
}

function normalizeTemperature(raw: unknown): string | number | null | undefined {
    if (raw === undefined) return undefined;
    if (raw === null) return null;
    if (typeof raw === "number" && Number.isFinite(raw)) return raw;
    if (typeof raw === "string") {
        const trimmed = raw.trim();
        return trimmed.length > 0 ? trimmed : undefined;
    }
    return undefined;
}

function extractTemperatureFromArt(art: Partial<AiSelfieArt> | null): string | number | null | undefined {
    if (!art) return undefined;
    const artRecord = art as Record<string, unknown>;
    if (Object.prototype.hasOwnProperty.call(artRecord, "temperature")) {
        return normalizeTemperature(artRecord.temperature);
    }
    if (Object.prototype.hasOwnProperty.call(artRecord, "temparature")) {
        return normalizeTemperature(artRecord.temparature);
    }
    return undefined;
}

function toReferenceStyleTemperature(value: string | number | null | undefined): number {
    if (value === null) return 0;
    if (typeof value === "number") return Number.isFinite(value) ? value : 0;
    if (typeof value === "string") {
        const parsed = Number(value.trim());
        return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
}

function toNumericTemperature(value: string | number | null | undefined): number {
    if (value === null) return 0;
    if (typeof value === "number") return Number.isFinite(value) ? value : 0;
    if (typeof value === "string") {
        const parsed = Number(value.trim());
        return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
}

/** Resolve relative art asset paths against the Mosida API host. */
export function resolveFetchableImageUrl(url: string): string {
    const t = (url || "").trim();
    if (!t) return t;
    if (t.startsWith("data:")) return t;
    if (/^https?:\/\//i.test(t)) return t;
    if (t.startsWith("//")) return `https:${t}`;
    const origin = MOSIDA_API_ORIGIN.replace(/\/$/, "");
    if (t.startsWith("/")) return `${origin}${t}`;
    return `${origin}/${t}`;
}

async function imageSourceToFile(source: string, fallbackBaseName: string): Promise<File> {
    const s = (source || "").trim();
    const resolved =
        s.startsWith("data:") || s.startsWith("blob:") || /^https?:\/\//i.test(s) ? s : resolveFetchableImageUrl(s);
    const response = await fetchImage(resolved);
    if (!response.ok) {
        throw new Error(`Image fetch failed (${fallbackBaseName}): ${response.status}`);
    }
    const blob = await response.blob();
    const extFromType = blob.type?.split("/")[1] || "png";
    let filename = `${fallbackBaseName}.${extFromType}`;
    try {
        const srcUrl = new URL(resolved);
        const last = srcUrl.pathname.split("/").filter(Boolean).pop();
        if (last) filename = decodeURIComponent(last);
    } catch {
        /* data URL or opaque */
    }
    return new File([blob], filename, { type: blob.type || "image/png" });
}

function canvasSizeForArt(art: Partial<AiSelfieArt> | null, slug?: string): string {
    // Caricature Portrait → generate-character-web with square canvas.
    if (
        isCaricaturePortraitFlow({
            slug,
            artName: art?.name,
            stationType: (art as { type?: string } | null)?.type,
        })
    ) {
        return "1:1";
    }
    if (
        isCaricatureHobbyAiSelfieArt(art) ||
        isCaricatureOrCartoonArt(art?.name, slug, (art as { type?: string } | null)?.type)
    ) {
        return "3:2";
    }
    return "1:1";
}

function canvasSizeForRequest(params: ProcessImageParams): string {
    const fromParams = String(params.canvas_size || "").trim();
    if (fromParams === "3:2" || fromParams === "1:1") return fromParams;
    return "1:1";
}

/** `background_url` for generation APIs: additional_params/preset first, then art `background_image`. */
function resolveBackgroundUrlForGeneration(params: ProcessImageParams): string {
    const fromBackground =
        typeof params.background === "string" ? params.background.trim() : String(params.background ?? "").trim();
    if (fromBackground) return fromBackground;
    return (params.background_image || "").trim();
}

/** Turn creative-system `local_path` / filename into a fetchable URL on AI services host. */
function resolveCreativeSystemLocalPath(pathOrName: string): string {
    const t = (pathOrName || "").trim();
    if (!t) return "";
    if (/^https?:\/\//i.test(t) || t.startsWith("data:")) return t;
    const origin = AI_SERVICES_ORIGIN.replace(/\/+$/, "");
    if (!origin) return t;
    if (t.startsWith("/")) return `${origin}${t}`;
    return `${origin}/outputs/${t}`;
}

function pickGeneratedImageUrl(json: any): string | undefined {
    const direct = [
        json?.image_url,
        json?.imageUrl,
        json?.output_url,
        json?.outputUrl,
        json?.result_url,
        json?.resultUrl,
        json?.url,
        json?.data?.image_url,
        json?.data?.output_url,
    ].find((u) => typeof u === "string" && u.trim().length > 0) as string | undefined;
    if (direct?.trim()) return direct.trim();

    // Mosida logo / creative-system generate returns local_path, not image_url.
    const localPath = [
        json?.local_path,
        json?.localPath,
        json?.output_filename,
        json?.outputFilename,
        json?.data?.local_path,
        json?.data?.output_filename,
    ].find((u) => typeof u === "string" && u.trim().length > 0) as string | undefined;
    if (localPath?.trim()) {
        const resolved = resolveCreativeSystemLocalPath(localPath.trim());
        return resolved || undefined;
    }
    return undefined;
}

/** No-logo variant from generate-character-web / reference-style (same idea as batch `image_url_nologo`). */
function pickGeneratedImageUrlNoLogo(json: any): string | undefined {
    const direct = [
        json?.image_url_nologo,
        json?.imageUrlNoLogo,
        json?.image_url_no_logo,
        json?.data?.image_url_nologo,
        json?.data?.imageUrlNoLogo,
    ].find((u) => typeof u === "string" && u.trim().length > 0) as string | undefined;
    if (direct?.trim()) return direct.trim();
    return undefined;
}

/**
 * Merges Mosida print API fields + local UI preset into params for image generation.
 * Non–caricature-hobby arts with `image` / `image_url` use `generate-reference-style-web`.
 */
export function buildProcessImageParamsFromArtAndPreset(options: {
    imageurl: string;
    art: Partial<AiSelfieArt> | null;
    slug: string | undefined;
    preset: TransformationProcessPreset;
    /** Print Your Own after /bg-remove Yes — cutout selfie, request transparent art. */
    forceTransparent?: boolean;
}): ProcessImageParams {
    const { imageurl, art, slug, preset, forceTransparent = false } = options;
    const prompt = buildCharacterPromptForArt(art, slug);
    const additional = parseAdditionalParamsFromApi(art?.additional_params);

    const backgroundFromApi = pickFirst<string>(additional, ["background_url", "backgroundUrl", "background"]);
    const background = backgroundFromApi ?? preset.backgroundUrl;
    const background_image = typeof art?.background_image === "string" ? art.background_image.trim() : "";

    const canvas_size = canvasSizeForArt(art, slug);

    const removeBgFromApi = pickFirst<boolean>(additional, ["remove_bg", "removeBg"]);
    const isPortraitFlow = isCaricaturePortraitFlow({
        slug,
        artName: art?.name,
    });
    const isPrintYourOwn = isPrintYourOwnFlow({
        slug,
        artName: art?.name,
    });
    // TEMP test: caricature-portrait + Pets only → is_transparent=false on character-web.
    const isPortraitPetSubject =
        isPortraitFlow && readPortraitSubject() === "pet";
    const useTransparent =
        (!isPortraitPetSubject && isPortraitFlow) ||
        (isPrintYourOwn && forceTransparent);
    // Portrait / Print Your Own cutout: don't ask generate API to remove BG again.
    const remove_bg = useTransparent
        ? false
        : removeBgFromApi !== undefined
          ? removeBgFromApi
          : preset.removeBg;

    const position = pickFirst<string>(additional, ["position"]) ?? "bottom";
    const logo_position =
        pickFirst<string>(additional, ["logo_postition", "logo_position"]) ??
        (typeof art?.logo_position === "string" ? art.logo_position.trim() : undefined) ??
        (typeof art?.logo_postition === "string" ? art.logo_postition.trim() : undefined) ??
        undefined;
    const temperatureFromArt = extractTemperatureFromArt(art);
    const temperature = temperatureFromArt !== undefined ? temperatureFromArt : parseTemperatureFromAdditional(additional);

    const extraFormFields: Record<string, string> = {};
    for (const [k, v] of Object.entries(additional)) {
        if (RESERVED_ADDITIONAL_KEYS.has(k)) continue;
        if (v === undefined || v === null) continue;
        extraFormFields[k] = typeof v === "object" ? JSON.stringify(v) : String(v);
    }

    let referenceStyle: ReferenceStyleParams | undefined;
    // Print Your Own uses /creative-system/generate (mosida-logo). Caricature portrait
    // always uses generate-character-web (with is_transparent).
    if (!isPrintYourOwn && !isPortraitFlow && !isCaricatureHobbyAiSelfieArt(art)) {
        const refRaw = aiSelfieArtStrictReferenceImageUrl(art || {}).trim();
        if (refRaw) {
            referenceStyle = {
                referenceImageUrl: refRaw,
            };
        }
    }

    return {
        imageurl,
        prompt,
        // Transparent flows must not send background_url.
        background: useTransparent ? undefined : background,
        background_image: useTransparent ? undefined : background_image || undefined,
        canvas_size,
        // Print Your Own → /creative-system/generate with station mosida-logo.
        station: isPrintYourOwn ? "mosida-logo" : undefined,
        is_logo: isPrintYourOwn ? true : undefined,
        useMosidaLogoGenerate: isPrintYourOwn ? true : undefined,
        remove_bg,
        position,
        logo_position: isPrintYourOwn ? logo_position || "top_right" : logo_position,
        temperature,
        // TEMP test: Pets → false; People portrait / print-your-own cutout → true.
        is_transparent: isPortraitPetSubject
            ? false
            : useTransparent
              ? true
              : undefined,
        extraFormFields: Object.keys(extraFormFields).length > 0 ? extraFormFields : undefined,
        referenceStyle,
    };
}

export interface ProcessImageResponse {
    success: boolean;
    data?: {
        processedImageUrl?: string;
        /** Character without Mosida logo when the API provides `image_url_nologo`. */
        processedImageUrlNoLogo?: string;
        [key: string]: any;
    };
    error?: string;
}

async function postGenerateCharacterFormData(params: ProcessImageParams): Promise<ProcessImageResponse> {
    const endpoint = `${AI_SERVICES_ORIGIN}/api/generate-character`;
    const form = new FormData();
    form.append("selfie", params.imageurl);
    form.append("selfie_url", params.imageurl);
    form.append("character_prompt", params.prompt);

    if (params.background) {
        form.append("background_url", params.background);
    }
    if (params.background_image) {
        form.append("background", params.background_image);
    }

    form.append("position", params.position || "bottom");
    if (params.logo_position?.trim()) {
        form.append("logo_position", params.logo_position.trim());
    }
    form.append("remove_bg", String(params.remove_bg));
    form.append("temperature", String(toNumericTemperature(params.temperature)));

    if (params.extraFormFields) {
        for (const [key, value] of Object.entries(params.extraFormFields)) {
            form.append(key, value);
        }
    }

    try {
        const res = await fetchWithTimeout(endpoint, {
            method: "POST",
            body: form,
            headers: {
                "X-API-Key": process.env.REACT_APP_FAST_API_SECRET_KEY || "",
            },
            timeoutMs: 120000,
        });
        if (!res.ok) {
            const text = await res.text();
            return { success: false, error: `Process API failed: ${res.status} ${text}` };
        }
        const json = await res.json();
        const processedImageUrl = json?.image_url ?? pickGeneratedImageUrl(json);
        const processedImageUrlNoLogo = pickGeneratedImageUrlNoLogo(json);
        return {
            success: true,
            data: {
                ...json,
                processedImageUrl,
                ...(processedImageUrlNoLogo ? { processedImageUrlNoLogo } : {}),
            },
        };
    } catch (e: any) {
        return { success: false, error: e?.message || "Network error" };
    }
}

/**
 * Print Your Own / snap-print: stamp Mosida logo via creative-system generate.
 * POST /creative-system/generate with image + logo_position + station=mosida-logo.
 * After /bg-remove Yes → also send transparent=true; No → omit transparent.
 */
async function postMosidaLogoGenerateFormData(params: ProcessImageParams): Promise<ProcessImageResponse> {
    const endpoint = `${AI_SERVICES_ORIGIN.replace(/\/+$/, "")}/creative-system/generate`;

    try {
        const imageFile = await imageSourceToFile(params.imageurl, "image");
        const form = new FormData();

        form.append("image", imageFile);
        const sourceUrl = (params.imageurl || "").trim();
        form.append("image_url", /^https?:\/\//i.test(sourceUrl) ? sourceUrl : "");
        form.append("logo_position", (params.logo_position || "top_right").trim() || "top_right");
        form.append("station", params.station || "mosida-logo");
        // Only when user chose Yes on /bg-remove (cutout + transparent art).
        if (params.is_transparent === true) {
            form.append("transparent", "true");
        }
        form.append("_regen", String(Date.now()));

        if (params.extraFormFields) {
            for (const [key, value] of Object.entries(params.extraFormFields)) {
                form.append(key, value);
            }
        }

        const headers: Record<string, string> = { accept: "*/*" };
        const apiKey = process.env.REACT_APP_FAST_API_SECRET_KEY;
        if (apiKey) {
            headers["X-API-Key"] = apiKey;
        }

        const res = await fetchWithTimeout(endpoint, {
            method: "POST",
            body: form,
            headers,
            timeoutMs: 120000,
        });

        if (!res.ok) {
            const text = await res.text();
            return { success: false, error: `Mosida logo generate API failed: ${res.status} ${text}` };
        }

        const contentType = res.headers.get("content-type") || "";
        if (contentType.indexOf("image/") === 0) {
            const blob = await res.blob();
            const processedImageUrl = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
            return { success: true, data: { processedImageUrl } };
        }

        const rawText = await res.text();
        let json: any = null;
        try {
            json = rawText ? JSON.parse(rawText) : {};
        } catch {
            json = { raw: rawText };
        }

        const processedImageUrl = pickGeneratedImageUrl(json) ?? json?.image_url;
        const processedImageUrlNoLogo = pickGeneratedImageUrlNoLogo(json);
        return {
            success: true,
            data: {
                ...json,
                processedImageUrl,
                ...(processedImageUrlNoLogo ? { processedImageUrlNoLogo } : {}),
            },
        };
    } catch (e: any) {
        return { success: false, error: e?.message || "Network error" };
    }
}

async function postGenerateCharacterWebFormData(params: ProcessImageParams): Promise<ProcessImageResponse> {
    const endpoint = `${AI_SERVICES_ORIGIN.replace(/\/+$/, "")}/generate-character-web`;

    try {
        const selfieFile = await imageSourceToFile(params.imageurl, "selfie");
        const form = new FormData();

        form.append("selfie", selfieFile);
        // Keep parity with current flow while switching endpoint.
        form.append("character_prompt", params.prompt);
        // Caricature portrait (is_transparent): omit background_url entirely.
        if (!params.is_transparent) {
            const backgroundUrl = resolveBackgroundUrlForGeneration(params);
            if (backgroundUrl) {
                form.append("background_url", backgroundUrl);
            }
        }
        form.append("use_gemini_compositing", "true");
        form.append("station", params.station || "caricature");
        form.append("position", params.position || "bottom");
        if (params.logo_position?.trim()) {
            form.append("logo_position", params.logo_position.trim());
        }
        form.append("scale", "1");
        form.append("canvas_size", canvasSizeForRequest(params));
        form.append("dpi", "300");
        form.append("upload_background_to_s3", "false");
        form.append("temperature", String(toNumericTemperature(params.temperature)));
        if (typeof params.is_transparent === "boolean") {
            form.append("is_transparent", params.is_transparent ? "true" : "false");
        }
        if (params.is_logo) {
            form.append("is_logo", "true");
        }

        if (params.extraFormFields) {
            for (const [key, value] of Object.entries(params.extraFormFields)) {
                form.append(key, value);
            }
        }

        const headers: Record<string, string> = { accept: "*/*" };
        const apiKey = process.env.REACT_APP_FAST_API_SECRET_KEY;
        if (apiKey) {
            headers["X-API-Key"] = apiKey;
        }

        const res = await fetchWithTimeout(endpoint, {
            method: "POST",
            body: form,
            headers,
            timeoutMs: 120000,
        });

        if (!res.ok) {
            const text = await res.text();
            return { success: false, error: `Generate character web API failed: ${res.status} ${text}` };
        }

        const contentType = res.headers.get("content-type") || "";
        if (contentType.indexOf("image/") === 0) {
            const blob = await res.blob();
            const processedImageUrl = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
            return { success: true, data: { processedImageUrl } };
        }

        const rawText = await res.text();
        let json: any = null;
        try {
            json = rawText ? JSON.parse(rawText) : {};
        } catch {
            json = { raw: rawText };
        }

        const processedImageUrl = pickGeneratedImageUrl(json) ?? json?.image_url;
        const processedImageUrlNoLogo = pickGeneratedImageUrlNoLogo(json);
        return {
            success: true,
            data: {
                ...json,
                processedImageUrl,
                ...(processedImageUrlNoLogo ? { processedImageUrlNoLogo } : {}),
            },
        };
    } catch (e: any) {
        return { success: false, error: e?.message || "Network error" };
    }
}

async function postReferenceStyleFormData(params: ProcessImageParams): Promise<ProcessImageResponse> {
    const ref = params.referenceStyle?.referenceImageUrl?.trim();
    if (!ref) {
        return postGenerateCharacterFormData(params);
    }

    const endpoint = `${AI_SERVICES_ORIGIN.replace(/\/+$/, "")}/generate-reference-style-web`;

    try {
        const sourceFile = await imageSourceToFile(params.imageurl, "source");
        const referenceFile = await imageSourceToFile(resolveFetchableImageUrl(ref), "reference");

        const form = new FormData();
        form.append("source", sourceFile);
        form.append("reference", referenceFile);
        form.append("temperature", String(toReferenceStyleTemperature(params.temperature)));
        form.append("reference_prompt", params.prompt);
        form.append("background_url", resolveBackgroundUrlForGeneration(params));
        form.append("canvas_size", canvasSizeForRequest(params));
        if (params.logo_position?.trim()) {
            form.append("logo_position", params.logo_position.trim());
        }

        if (params.extraFormFields) {
            for (const [key, value] of Object.entries(params.extraFormFields)) {
                form.append(key, value);
            }
        }

        const headers: Record<string, string> = { accept: "*/*" };
        const apiKey = process.env.REACT_APP_FAST_API_SECRET_KEY;
        if (apiKey) {
            headers["X-API-Key"] = apiKey;
        }

        const res = await fetchWithTimeout(endpoint, {
            method: "POST",
            body: form,
            headers,
            timeoutMs: 120000,
        });

        if (!res.ok) {
            const text = await res.text();
            return { success: false, error: `Reference style API failed: ${res.status} ${text}` };
        }

        const contentType = res.headers.get("content-type") || "";
        if (contentType.indexOf("image/") === 0) {
            const blob = await res.blob();
            const processedImageUrl = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
            return { success: true, data: { processedImageUrl } };
        }

        const rawText = await res.text();
        let json: any = null;
        try {
            json = rawText ? JSON.parse(rawText) : {};
        } catch {
            json = { raw: rawText };
        }

        const processedImageUrl = pickGeneratedImageUrl(json) ?? json?.image_url;
        const processedImageUrlNoLogo = pickGeneratedImageUrlNoLogo(json);
        return {
            success: true,
            data: {
                ...json,
                processedImageUrl,
                ...(processedImageUrlNoLogo ? { processedImageUrlNoLogo } : {}),
            },
        };
    } catch (e: any) {
        return { success: false, error: e?.message || "Network error" };
    }
}

async function postFormData(params: ProcessImageParams): Promise<ProcessImageResponse> {
    if (params.useMosidaLogoGenerate) {
        return postMosidaLogoGenerateFormData(params);
    }
    const refUrl = params.referenceStyle?.referenceImageUrl?.trim();
    if (refUrl) {
        return postReferenceStyleFormData(params);
    }
    return postGenerateCharacterWebFormData(params);
}

export async function processImageWithRetry(params: ProcessImageParams, retries = 2, delayMs = 1500): Promise<any> {
    let attempt = 0;
    let lastError: string | undefined;
    while (attempt <= retries) {
        const result = await postFormData(params);
        if (result.success) return result;
        lastError = result.error;
        attempt++;
        if (attempt <= retries) await new Promise(r => setTimeout(r, delayMs));
    }
    return { success: false, error: lastError || "Unknown error" };
}